import { Helmet } from "react-helmet";
import React, { useEffect, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";
import Header from "../components/header";
import Footer from "../components/footer";
import { AuthenticateRoute } from "./authenticateRoute";
import AccessDenied from "../pages/access-denied";
import ErrorDialogBox from "../utils/interceptors/errorDialogBox";
import initializePendo from '../pendo';
import { loadABTasty } from "../utils/loadABTasty";
import { isResMedDomainUser } from "../utils/common";

// Lazy-loaded components
const Login = lazy(() => import("../pages/home"));
const SomethingWentWrong = lazy(() => import("../pages/something-went-wrong"));
const OrderHistory = lazy(() => import("../pages/order-history"));
const Loader = lazy(() => import("../components/loader"));
const Snackbar = lazy(() => import("../components/flashMessages"));
const LoaderInit = lazy(() => import("../components/loader/LoaderInit"));
const SessionTimeoutWarningDialog = lazy(() =>
  import("../components/SessionTimeoutWarningDialog")
);
const PlaceOrder = lazy(() => import("../pages/place-order"));
const SplashMessage = lazy(() => import("../components/SplashMessage"));
const UserVerification = lazy(() => import("../pages/home/userVerification"));
const PageNotFound = lazy(() => import("../pages/page-not-found"));
const ResetPassword = lazy(() => import("../pages/home/resetPassword"));
const Registration = lazy(() => import("../pages/registeration"));
const ReturnCenter = lazy(() => import("../pages/return-center"));
const ReturnHistory = lazy(() => import("../pages/return-history"));
const Warranty = lazy(() => import("../pages/warranty-lookup"));
const MaskGuarantee = lazy(() => import("../pages/mask-guarantee"));
const MaskGuaranteeHistory = lazy(() => import("../pages/mg-history"));
const Cto = lazy(() => import("../pages/cto"));
const MyCompany = lazy(() => import("../pages/my-company"));
const Payments = lazy(() => import("../pages/payments"));
const MyAccount = lazy(() => import("../pages/my-account"));
const ContactUs = lazy(() => import("../pages/contact-us"));
const DVA = lazy(() => import("../pages/dva"));
const DraftsDva = lazy(() => import("../pages/dva/drafts"));
const ClaimsDva = lazy(() => import("../pages/dva/claimsHistory"));
const DVAManualsAndForms = lazy(() => import("../pages/dva-manuals-forms"));

const Page = () => {
  const { user, error401, errorSyncRequired, loggedIn } = useSelector(
    (state) => state.authentication
  );
  const custId = useSelector((state) => state.cartDetails);
   const { accountName } = useSelector((state) => state.setUser);
  useEffect(() => {
    if (user) {
      let userType = "";
      if (user?.CP_IS_SALESREP && user?.CP_IS_SALESREP === "Y") {
        userType = "Sales Rep";
      } else if (user?.CP_IS_CSR && user?.CP_IS_CSR === "Y") {
        userType = "CSR";
      } else {
        userType = "Customer";
      }
      ReactGA.gtag("set", "user_properties", {
        userRole: userType,
      });
      window.digitalData = window.digitalData || {};
      window.digitalData.customerId = String(user?.CP_ACCOUNT_ID);
      window.digitalData.customerCountry = String(user?.CP_COUNTRY);
      window.digitalData.customerRegion = String(user?.REGION);
      // console.log('tst', window.digitalData.customerId);
      if(user.CP_ACCOUNT_ID) {
        loadABTasty();
      }
    }
  }, [user?.CP_IS_SALESREP, user?.CP_IS_CSR]);
  useEffect(() => {
    if (user?.CP_ACCOUNT_ID && window?.OptanonActiveGroups?.includes('C0002') && accountName) {
      initializePendo({
        accountID: user?.CP_ACCOUNT_ID,
        email: user?.CP_DEFAULT_EMAIL || user?.email,
        accountName,
        ros_country: user?.CP_COUNTRY || "",
        ros_region: user?.REGION || "",
      });
    }
  }, [user, window?.OptanonActiveGroups, accountName]);


  useEffect(() => {
    if (custId?.uniqueCustId) {
      ReactGA.gtag("set", "user_properties", {
        user_id: custId?.uniqueCustId,
      });
    }
  }, [custId?.uniqueCustId]);

  const userEmail = useSelector(
    (state) => state.authentication?.user?.CP_USER_NAME || ""
  );

  const isEligibleForMyCompany =
    user?.CP_USER_ROLE?.includes("Admin") &&
    user?.CP_IS_CSR !== "Y" &&
    user?.CP_IS_SALESREP !== "Y" &&
    !isResMedDomainUser(userEmail);

  return (
    <>
      <Helmet>
        <title> Online Resmed for Business</title>
      </Helmet>
      <Header />
      <Snackbar />
      <ErrorDialogBox />
      <SessionTimeoutWarningDialog />
      <LoaderInit />
      <Loader />
      {loggedIn && <SplashMessage />}
      <div className="main-body">
        {error401 ? (
          <SomethingWentWrong />
        ) : errorSyncRequired ? (
          <SomethingWentWrong message="Customer must be synced to cloud before login." />
        ) : (
          <Suspense fallback={<Loader loading />}>
            <Routes>
              {/* <Route path="/" element={<Login />} /> */}
              <Route path="/login" element={<Login />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/new-account" element={<Registration />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accessDenied" element={<AuthenticateRoute path="/accessDenied"><AccessDenied /></AuthenticateRoute>} />
              <Route path="/pageNotFound" element={<AuthenticateRoute path="/pageNotFound"><PageNotFound /></AuthenticateRoute>} />
              <Route path="/somethingWentWrong" element={<SomethingWentWrong />} />
              <Route path="/dashboard" element={<AuthenticateRoute path="/dashboard"><Registration /></AuthenticateRoute>} />
              <Route path="/order" element={<AuthenticateRoute path="/order"><PlaceOrder /></AuthenticateRoute>} />
              <Route path="/my-company" element={isEligibleForMyCompany ? <AuthenticateRoute path="/my-company"><MyCompany /></AuthenticateRoute> : <AccessDenied />} />
              <Route path="/order-history" element={<AuthenticateRoute path="/order-history"><OrderHistory /></AuthenticateRoute>} />
              {/* <Route path="/order" element={<AuthenticateRoute><PlaceOrder /></AuthenticateRoute>} /> */}
              <Route path="/return" element={<AuthenticateRoute path="/return"><ReturnCenter /></AuthenticateRoute>} />
              <Route path="/return/:serial/:productId/:pdate?" element={<AuthenticateRoute path="/return/:serial/:productId/:pdate?"><ReturnCenter /></AuthenticateRoute>} />
              <Route path="/return-history" element={<AuthenticateRoute path="/return-history"><ReturnHistory /></AuthenticateRoute>} />
              <Route path="/warranty" element={<AuthenticateRoute path="/warranty"><Warranty /></AuthenticateRoute>} />
              <Route path="/mask-guarantee" element={<AuthenticateRoute path="/mask-guarantee"><MaskGuarantee /></AuthenticateRoute>} />
              <Route path="/mask-guarantee-history" element={<AuthenticateRoute path="/mask-guarantee-history"><MaskGuaranteeHistory /></AuthenticateRoute>} />
              <Route path="/cto" element={<AuthenticateRoute path="/cto"><Cto /></AuthenticateRoute>} />
              <Route path="/my-account" element={<AuthenticateRoute path="/my-account"><MyAccount /></AuthenticateRoute>} />
              <Route path="/userVerification" element={<AuthenticateRoute path="/userVerification"><UserVerification /></AuthenticateRoute>} />
              <Route path="/payments" element={<AuthenticateRoute path="/payments"><Payments /></AuthenticateRoute>} />
              <Route path="/dvaManualForms" element={<AuthenticateRoute path="/dvaManualForms"><DVAManualsAndForms /></AuthenticateRoute>}/>
              <Route path="/dva" element={<AuthenticateRoute path="/dva"><DVA /></AuthenticateRoute>}/>
              <Route path="/drafts" element={<AuthenticateRoute path="/drafts"><DraftsDva /></AuthenticateRoute>}/>
              <Route path="/claims" element={<AuthenticateRoute path="/claims"><ClaimsDva /></AuthenticateRoute>}/>
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </Suspense>
        )}
      </div>
      <Footer />
    </>
  );
};
export default Page;
